import { useEffect, useState } from "react";
import { dashboardApi, alertsApi } from "@/api";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { AlertTriangle, Users, Shield, Zap, TrendingUp, Activity, CheckCircle2, XCircle } from "lucide-react";

function RiskHeatCell({ dept, score }) {
  let bg = "bg-green-500/20 border-green-500/30 text-green-400";
  if (score >= 81) bg = "bg-red-900/40 border-red-500/50 text-red-300";
  else if (score >= 61) bg = "bg-red-500/20 border-red-500/30 text-red-400";
  else if (score >= 31) bg = "bg-amber-500/15 border-amber-500/30 text-amber-400";

  return (
    <div className={`heatmap-cell border rounded-sm p-3 cursor-pointer ${bg}`} data-testid={`heatmap-${dept.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs tracking-widest uppercase font-bold text-inherit opacity-70 mb-1">{dept}</div>
      <div className="text-2xl font-mono font-medium">{score}</div>
      <div className="text-xs opacity-60 mt-1">Risk Score</div>
    </div>
  );
}

function KPICard({ label, value, sub, icon: Icon, color = "text-primary", danger = false }) {
  return (
    <div className={`bg-card border rounded-sm p-4 fade-in-up ${danger && parseInt(value) > 0 ? "border-red-500/30" : "border-border"}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs tracking-widest uppercase text-muted-foreground font-bold">{label}</span>
        <Icon size={16} className={color} />
      </div>
      <div className={`text-3xl font-mono font-medium ${color}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function AlertSeverityDot({ severity }) {
  const colors = { critical: "bg-red-500", high: "bg-orange-500", medium: "bg-amber-400", low: "bg-slate-400" };
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[severity] || "bg-slate-400"} shrink-0 mt-1`} />;
}

export default function EnterpriseDashboard() {
  const [overview, setOverview] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([dashboardApi.getOverview(), alertsApi.getAll({ status: "open" })])
      .then(([ov, al]) => {
        setOverview(ov.data);
        setAlerts(al.data.slice(0, 6));
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex-1 p-6 flex items-center justify-center h-full">
      <div className="text-primary font-mono text-sm animate-pulse">LOADING ENTERPRISE DATA...</div>
    </div>
  );

  const { total_personnel, high_risk_count, do_not_assign_count, active_alerts, critical_alerts, enterprise_risk_score, department_risk, trend, legal_vs_safe, active_irrops } = overview || {};

  return (
    <div className="p-6 space-y-6 fade-in-up" data-testid="enterprise-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Enterprise Overview</div>
          <h1 className="text-2xl font-black font-heading text-foreground">Command Dashboard</h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground border border-border rounded-sm px-3 py-2">
          <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          Live · {new Date().toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Total Personnel" value={total_personnel} sub="Monitored system-wide" icon={Users} />
        <KPICard label="High Risk" value={high_risk_count} sub={`+ ${do_not_assign_count} Do Not Assign`} icon={AlertTriangle} color="text-red-400" danger />
        <KPICard label="Active Alerts" value={active_alerts} sub={`${critical_alerts} critical`} icon={Shield} color="text-amber-400" danger />
        <KPICard label="Active IRROPS" value={active_irrops} sub="Live disruptions" icon={Activity} color="text-orange-400" />
      </div>

      {/* Risk Heatmap + Legal vs Safe */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-4">
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-4">Department Risk Heat Map</div>
          <div className="grid grid-cols-3 gap-3">
            {department_risk && Object.entries(department_risk).map(([dept, score]) => (
              <RiskHeatCell key={dept} dept={dept} score={score} />
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {/* Legal vs Safe */}
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-3">Legal vs Safe Gap</div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Legal Compliant</span>
                  <span className="font-mono text-green-400">{legal_vs_safe?.legal_percentage}%</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: `${legal_vs_safe?.legal_percentage}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Truly Safe</span>
                  <span className="font-mono text-amber-400">{legal_vs_safe?.safe_percentage}%</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full" style={{ width: `${legal_vs_safe?.safe_percentage}%` }} />
                </div>
              </div>
              <div className="pt-2 border-t border-border">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Safety Gap</span>
                  <span className="text-lg font-mono font-medium text-red-400">{legal_vs_safe?.gap}%</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{legal_vs_safe?.gap}% of personnel are legal but not fully safe</p>
              </div>
            </div>
          </div>

          {/* Enterprise Risk Score */}
          <div className="bg-card border border-amber-500/20 rounded-sm p-4">
            <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-2">Enterprise Risk Score</div>
            <div className="text-5xl font-mono font-medium text-amber-400">{enterprise_risk_score}</div>
            <div className="text-xs text-muted-foreground mt-1">/ 100 · Elevated</div>
          </div>
        </div>
      </div>

      {/* Trend Chart + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-card border border-border rounded-sm p-4">
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-4">7-Day Fatigue Risk Trend</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trend} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(222, 20%, 15%)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(215, 20%, 65%)" }} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(215, 20%, 65%)" }} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: "hsl(222, 47%, 8%)", border: "1px solid hsl(222, 20%, 15%)", borderRadius: "2px", fontSize: 11 }} />
              <Line type="monotone" dataKey="enterprise" stroke="hsl(217, 91%, 60%)" strokeWidth={2} dot={false} name="Enterprise" />
              <Line type="monotone" dataKey="crew" stroke="hsl(38, 92%, 50%)" strokeWidth={1.5} dot={false} name="Crew" strokeDasharray="4 2" />
              <Line type="monotone" dataKey="engineering" stroke="hsl(142, 70%, 45%)" strokeWidth={1.5} dot={false} name="Engineering" strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex gap-4 mt-2">
            {[["Enterprise", "hsl(217,91%,60%)"], ["Crew", "hsl(38,92%,50%)"], ["Engineering", "hsl(142,70%,45%)"]].map(([name, color]) => (
              <div key={name} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="inline-block w-3 h-0.5" style={{ background: color }} />
                {name}
              </div>
            ))}
          </div>
        </div>

        {/* Live Alerts */}
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-4">
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-3">Active Alerts</div>
          <div className="space-y-2 overflow-y-auto max-h-[230px]">
            {alerts.map(alert => (
              <div key={alert.id} className={`p-2 rounded-sm border-l-2 bg-background/60 ${
                alert.severity === "critical" ? "border-red-500" :
                alert.severity === "high" ? "border-orange-500" :
                alert.severity === "medium" ? "border-amber-500" : "border-slate-500"
              }`} data-testid={`alert-item-${alert.id}`}>
                <div className="flex items-start gap-2">
                  <AlertSeverityDot severity={alert.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-foreground truncate">{alert.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">{alert.department}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
